import { Box, Container, Flex, Text, Heading, Grid } from '@radix-ui/themes';
import { PlusIcon, MinusIcon } from '@radix-ui/react-icons';
import { Image } from '@trashlab/sitesapp/src/organizations/piedmont/components/shared/image';
import { useTenantContext } from '@trashlab/sitesapp/src/organizations/piedmont/contexts/use-tenant-context';
import * as Accordion from '@radix-ui/react-accordion';
import { cn } from '@trashlab/sitesapp/src/@/lib/utils';
import { IconArrowDown } from '@trashlab/sitesapp/src/organizations/piedmont/template/components/icons/icons';
import { useVariantValue } from '@trashlab/sitesapp/src/organizations/piedmont/lib/utils/get-variant-value';
import { useMemo } from 'react';
import { asAddress } from '@trashlab/api/src/library/server/utils/json-compatibility';
import {
  convertYouTubeToEmbed,
  isVideoUrl,
} from '@trashlab/sitesapp/src/organizations/piedmont/lib/utils/youtube-url';
import { FAQSchema } from '@trashlab/sitesapp/src/organizations/piedmont/components/seo/schemas/faq-schema';

// Best Deal Dumpsters org ID
const BEST_DEAL_ORG_ID = 'd74bb19c-004b-479d-adaa-9e846d25dac7';

type VariantValue = {
  value: string;
  orderIndex?: number;
};

type FaqType =
  | 'home'
  | 'how-to'
  | 'residential'
  | 'commercial'
  | 'rolloff'
  | 'portableToilet';

type FaqVariantKey =
  | 'HOME_FAQ_QUESTION'
  | 'HOME_FAQ_ANSWER'
  | 'HOW_TO_FAQ_QUESTION'
  | 'HOW_TO_FAQ_ANSWER'
  | 'RESIDENTIAL_FAQ_QUESTION'
  | 'RESIDENTIAL_FAQ_ANSWER'
  | 'COMMERCIAL_FAQ_QUESTION'
  | 'COMMERCIAL_FAQ_ANSWER'
  | 'ROLLOFF_FAQ_QUESTION'
  | 'ROLLOFF_FAQ_ANSWER'
  | 'PORTABLE_TOILET_FAQ_QUESTION'
  | 'PORTABLE_TOILET_FAQ_ANSWER';

export const FAQ = ({
  accordionOnly = false,
  faqType = 'home',
  customTitle,
  customSubtitle,
  customDescription,
  withBackground = false,
}: {
  accordionOnly?: boolean;
  faqType?: FaqType;
  customTitle?: string;
  customSubtitle?: string;
  customDescription?: string;
  withBackground?: boolean;
}) => {
  const { website, isEditMode } = useTenantContext();
  const orgAddress = asAddress(website?.organization?.address);
  const city = orgAddress?.city ?? 'San Francisco';
  const state = orgAddress?.state ?? 'CA';

  const defaultHomeFaqs = [
    {
      question: "What's Dumpster Rental pickup process?",
      answer:
        "Once your dumpster is filled and you're ready for it to be hauled away, just give us a call. We'll schedule a pick-up at a time that's convenient for you. It's as simple as that.",
    },
    {
      question: 'What items can I dispose of in the dumpster?',
      answer:
        "Once your dumpster is filled and you're ready for it to be hauled away, just give us a call. We'll schedule a pick-up at a time that's convenient for you. It's as simple as that.",
    },
    {
      question: 'Can I rent more than one dumpster at a time?',
      answer:
        "Absolutely! We can accommodate projects that require multiple dumpsters. If you're unsure of your needs, our team is available to help determine the best waste management solution for your project.",
    },
    {
      question: 'How far in advance should I reserve a dumpster?',
      answer:
        'We recommend booking your dumpster as early as possible to ensure availability, especially for larger dumpsters or during peak times.',
    },
  ];

  const defaultHowToFaqs = [
    {
      question: `How do I go about renting a dumpster with ${website?.organization.name}?`,
      answer:
        'You can easily go about renting a dumpster with us either through our online booking system or by giving us a call. Our friendly customer service team will guide you through the process and help you select the right dumpster for your needs.',
    },
    {
      question: 'What forms of payment do you accept?',
      answer:
        'All forms of payment are made electronically, and we accept the following forms of payment: Visa, Mastercard, American Express, Apple Pay, Discover, Google Pay, Paypal, Paypal Credit, Venmo, and Cash App. We unfortunately do not take cash or checks.',
    },
    {
      question: 'What types of waste are not allowed in the dumpsters?',
      answer:
        'While our dumpsters can handle a wide variety of waste types, we do not accept hazardous materials, including but not limited to motor oil, weapons, tires, asbestos, sealed tanks/drums, chemicals, propane tanks and pesticides.',
    },
    {
      question: 'What happens if I exceed the weight limit of the dumpster?',
      answer: `At ${website?.organization.name}, we strongly believe in fairness and transparency. Our pricing model is built on a 'pay for what you use' principle. If your load exceeds the dumpster's weight limit, you won't be charged a flat overage fee. Instead, you'll only pay the additional cost for the extra weight based on the rates provided in our additional prices chart. Click here to view items that contain additional pricing.`,
    },
    {
      question: 'Are your services available on weekends?',
      answer:
        "Yes, we strive to accommodate to the customers' preferred schedule, that's why we offer renting a dumpster, as well as deliveries and pickups seven days a week.",
    },
    {
      question:
        'Do I need to be present during the dumpster delivery or pick-up?',
      answer: `Yes. It's ${website?.organization.name}'s Policy for an adult to be present when we deliver your dumpsters. Before renting a dumpster with us, please make sure to coordinate and to have an adult present during time of delivery. `,
    },
  ];

  const defaultResidentialFaqs = [
    {
      question: 'What is the ideal dumpster size for a residential cleanout?',
      answer:
        'The ideal dumpster size for a residential clean-out depends on the volume of waste. A 12-yard or 22-yard dumpster is usually sufficient for most home projects.',
    },
    {
      question: 'How much can a residential dumpster hold?',
      answer:
        'The volume a residential dumpster can hold depends on its size. For example, a 12-yard dumpster can typically hold up to 12 cubic yards of material.',
    },
    {
      question: 'Can a residential dumpster be placed on the street?',
      answer:
        'Yes, in most cases, we can place your dumpster on the street. We are aware of the laws and ordinances of the areas we services and will handle all requirements necessary for your dumpster rental. No hassle with planning, leave that part to us.',
    },
    {
      question: "What items can't go into a residential dumpster?",
      answer:
        "Hazardous materials, weapons, propane tanks, tires, and batteries are among the items that can't go into a residential dumpster.",
    },
    {
      question: 'Is there a time limit for residential dumpster rentals?',
      answer:
        'There is no time limit for renting a dumpster. The rental cost covers the first 24 hours, ensuring you have ample time for your disposal needs. After the initial period, a nominal fee of just $50 is applied for each additional day. This flexibility allows you to keep the dumpster for as long as your project requires.',
    },
    {
      question: "Can I move the dumpster once it's placed?",
      answer:
        'We have a very strict safety policy against moving, pushing, lifting, pulling or altering the state of the dumpster after it is placed for any reason. Violators will be charged a $1,000 fine. If you need to have your dumpster(s) moved, please call us.',
    },
  ];

  const defaultCommercialFaqs = [
    {
      question: `What sizes of commercial dumpsters does ${website?.organization?.name} offer?`,
      answer:
        'We offer a variety of dumpster sizes suitable for contractor needs, ranging from 7 Yard Roll Off Dumpsters for smaller projects to our 40 Yard Roll Off Dumpsters option which provides two individual 22 Yard Roll Off Dumpsters that can be placed in different locations around a single facility',
    },
    {
      question: 'How long can a business rent a commercial roll off dumpster?',
      answer: `At ${website?.organization?.name}, businesses can rent a commercial roll-off dumpster for both short-term and long-term periods. Our flexible rental durations cater to your specific project needs, whether its for a quick clean-up lasting a few days or an extended project spanning several weeks.`,
    },
    {
      question:
        'Can a business schedule regular pickups for long-term projects?',
      answer: `Yes, at ${website?.organization?.name}, businesses engaged in long-term projects can schedule regular pickups for their commercial roll-off dumpster rentals. We offer customizable pickup schedules to ensure your waste management needs are continuously met without interruption. Whether you require weekly, bi-weekly, or a more tailored pickup frequency, our team is ready to accommodate your project's demands, keeping your facility clean and compliant.`,
    },
    {
      question: 'Where is the best place to put a roll off dumpster rental?',
      answer:
        'Find a flat area that has at least 3 feet of clearance around each side of the dumpster, at least 22 feet of unobstructed height and 34 feet of unobstructed room in front for a service pickup. If a paved driveway is being used, consider covering the driveway or putting wood underneath your dumpster to reduce the risk of damage.',
    },
    {
      question: `Is ${website?.organization?.name} available for commercial dumpster rentals?`,
      answer: `Absolutely, ${website?.organization?.name} is proud to provide same day delivery options for our commercial dumpster rentals. This service is designed to meet the urgent disposal needs of businesses, ensuring that your facility can proceed operating without delay. Whether you're tackling a sudden clean-up, a large-scale renovation, or any project in between, we're equipped to deliver the right dumpster to your site promptly, reflecting our commitment to convenience and efficiency.`,
    },
    {
      question:
        'Are heavy duty roll off dumpsters available for construction debris?',
      answer:
        'Absolutely, our 7 Yard Roll Off Dumpster is a heavy-duty dumpster designed to handle construction and demolition debris, including concrete, roofing, brick, and asphalt projects.',
    },
  ];

  const defaultRolloffFaqs = [
    {
      question: 'What dumpster sizes are available for construction projects?',
      answer:
        'We offer a variety of dumpster sizes suitable for construction needs, ranging from 7-yard dumpsters for smaller projects to 40-yard dumpsters for substantial construction work.',
    },
    {
      question: 'Are there any specific rental terms for rolloff dumpsters?',
      answer:
        'The rental terms for rolloff dumpsters are flexible and can be tailored to the specific needs and duration of the project.',
    },
    {
      question:
        'Can I schedule regular pickups for long-term construction projects?',
      answer:
        'Yes, you can schedule regular pickups for long-term projects to ensure a clean, efficient, safe, and compliant worksite.',
    },
    {
      question: 'Are there any special rates available for larger projects?',
      answer:
        'We do offer special rates for larger projects, especially for renting a dumpster long-term, as well as regular pickup schedules.',
    },
    {
      question: 'Is same-day service available for rolloff dumpster rentals?',
      answer:
        'Yes, we provide same-day service for rolloff dumpster rentals, based on availability.',
    },
    {
      question: 'Do you provide heavy-duty dumpsters for construction debris?',
      answer:
        'Absolutely, our heavy-duty dumpsters are designed to handle construction and demolition debris, including concrete, roofing, brick, and asphalt projects.',
    },
  ];

  const defaultPortableToiletFaqs = [
    {
      question: 'What types of portable toilets do you offer?',
      answer:
        'We offer a range of portable toilet options, including standard units, ADA-compliant accessible units, luxury restroom trailers for events, and hand-washing stations.',
    },
    {
      question: 'How often are your portable toilets serviced?',
      answer:
        'Our standard service schedule includes weekly maintenance, but we offer more frequent servicing for high-traffic locations. Each service includes waste removal, sanitization, restocking of supplies, and deodorizing.',
    },
    {
      question: 'Can portable toilets be used in any weather condition?',
      answer:
        'Yes, our portable toilets are designed to withstand various weather conditions. We offer climate-controlled options for extreme weather and can provide recommendations based on your specific needs and location.',
    },
    {
      question:
        'How far in advance should I book portable toilets for an event?',
      answer:
        'We recommend booking at least 2-3 weeks in advance for standard events, and 1-2 months for large events or during peak season (summer months) to ensure availability and proper planning.',
    },
    {
      question: 'Do I need any permits to place portable toilets?',
      answer:
        'Permit requirements vary by location. For public events or construction sites, permits may be required. Our team can guide you on local regulations and help with the placement to ensure compliance.',
    },
    {
      question: 'What amenities come with your portable toilets?',
      answer:
        'Our standard units include toilet paper, hand sanitizer, and ventilation. Premium units may include handwashing stations, mirrors, interior lighting, and upgraded interiors. Luxury restroom trailers feature running water, flushing toilets, climate control, and high-end finishes.',
    },
  ];

  // Best Deal Dumpsters specific FAQs - based on their "Things to Know" page
  const bestDealFaqs = [
    {
      question: 'What items are NOT accepted in Best Deal dumpsters?',
      answer:
        'We do not accept the following for disposal: tires, hazardous materials, propane tanks, rock/dirt/concrete, paint, car batteries, products containing Freon (such as refrigerators and A/C units), creosote-filled materials (such as telephone poles and railroad ties), and 55-gallon drums.',
    },
    {
      question: 'How should I load the dumpster?',
      answer:
        'Do NOT load above the top of the container. We take extra precaution not to disturb your property while delivering or picking up our dumpster. To ensure that your property is left undisturbed, please do not fill the container above the top. This will make for a quick and easy pick up.',
    },
    {
      question: 'Can I recycle materials like shingles?',
      answer:
        'Yes! If you want to recycle shingles, the dumpster cannot contain any other materials. We can accept mixed loads, but it will render it un-recyclable. Please mention your recycling needs when you place your order.',
    },
    {
      question: 'Why does Best Deal use smaller trucks?',
      answer:
        'Because Best Deal specializes in driveway deliveries, we use smaller trucks to protect your driveway from cracks and damage. Our trucks are designed to minimize impact on residential properties.',
    },
    {
      question: 'How do I rent a dumpster with Best Deal?',
      answer:
        'You can easily rent a dumpster through our online booking system or by giving us a call at 502-396-0666. Our friendly customer service team will guide you through the process and help you select the right dumpster size for your needs.',
    },
    {
      question: 'What areas does Best Deal Dumpsters serve?',
      answer:
        'Best Deal Dumpsters is a locally owned company serving Louisville, KY and the surrounding communities. Contact us to confirm service availability in your area.',
    },
  ];

  const isBestDeal = website?.organization?.id === BEST_DEAL_ORG_ID;

  const getVariantKey = (type: FaqType): [FaqVariantKey, FaqVariantKey] => {
    switch (type) {
      case 'home':
        return ['HOME_FAQ_QUESTION', 'HOME_FAQ_ANSWER'];
      case 'how-to':
        return ['HOW_TO_FAQ_QUESTION', 'HOW_TO_FAQ_ANSWER'];
      case 'residential':
        return ['RESIDENTIAL_FAQ_QUESTION', 'RESIDENTIAL_FAQ_ANSWER'];
      case 'commercial':
        return ['COMMERCIAL_FAQ_QUESTION', 'COMMERCIAL_FAQ_ANSWER'];
      case 'rolloff':
        return ['ROLLOFF_FAQ_QUESTION', 'ROLLOFF_FAQ_ANSWER'];
      case 'portableToilet':
        return ['PORTABLE_TOILET_FAQ_QUESTION', 'PORTABLE_TOILET_FAQ_ANSWER'];
      default:
        return ['HOME_FAQ_QUESTION', 'HOME_FAQ_ANSWER'];
    }
  };

  const getDefaultFaqs = (type: FaqType) => {
    // Best Deal org uses consistent FAQs across all types
    if (isBestDeal) {
      return bestDealFaqs;
    }

    switch (type) {
      case 'home':
        return defaultHomeFaqs;
      case 'how-to':
        return defaultHowToFaqs;
      case 'residential':
        return defaultResidentialFaqs;
      case 'commercial':
        return defaultCommercialFaqs;
      case 'rolloff':
        return defaultRolloffFaqs;
      case 'portableToilet':
        return defaultPortableToiletFaqs;
    }
  };

  const [questionKey, answerKey] = getVariantKey(faqType);
  const questions = useVariantValue<VariantValue[]>(questionKey, []);
  const answers = useVariantValue<VariantValue[]>(answerKey, []);

  const faqs = useMemo(() => {
    if (!questions.length && !answers.length) {
      return getDefaultFaqs(faqType);
    }

    const orderIndexes = [
      ...new Set([
        ...questions.map((q) => q.orderIndex ?? 0),
        ...answers.map((a) => a.orderIndex ?? 0),
      ]),
    ].sort((a, b) => a - b);

    return orderIndexes
      .map((index) => {
        const question = questions.find((q) => q.orderIndex === index)?.value;
        const answer = answers.find((a) => a.orderIndex === index)?.value;
        const defaultFaq = getDefaultFaqs(faqType)[index];

        return {
          question: question ?? defaultFaq?.question ?? '',
          answer: answer ?? defaultFaq?.answer ?? '',
        };
      })
      .filter((faq) => faq.question && faq.answer);
  }, [questions, answers, faqType, website?.organization?.name, isBestDeal]);

  const defaultTitle = `Common questions about dumpster rental in ${city}, ${state}`;
  const defaultSubtitle = 'Best roll off dumpster rental company';

  const faqTitle = useVariantValue('FAQ_TITLE', defaultTitle);
  const faqSubtitle = useVariantValue('FAQ_SUBTITLE', defaultSubtitle);

  const title = customTitle ?? faqTitle;
  const subtitle = customSubtitle ?? faqSubtitle;

  const rentalVideoTitle = useVariantValue(
    'RENTAL_MEDIA_TITLE',
    `Dumpster Rental Guide`,
  );
  const rentalVideoSubtitle = useVariantValue(
    'RENTAL_MEDIA_SUBTITLE',
    `Need help choosing the right size roll-off dumpster? View our guide to learn how to rent a ${
      website?.organization?.name || 'company'
    } dumpster in ${city}, ${state}.`,
  );
  const rentalVideoUrl = useVariantValue(
    'RENTAL_MEDIA_URL',
    '/default/rental-media.svg',
  );

  // Detect if the URL is a video (YouTube, Vimeo, etc.) or an image
  const isVideo = isVideoUrl(rentalVideoUrl);
  const isImage = !isVideo;

  // Convert YouTube watch URLs to embed format for iframe compatibility
  const embedUrl = isVideo
    ? convertYouTubeToEmbed(rentalVideoUrl)
    : rentalVideoUrl;

  // Render FAQs in edit mode with all content visible
  const renderEditModeFAQs = () => {
    return (
      <div className="space-y-6">
        {faqs.map((faq, index) => (
          <div key={`${questionKey}-${index}`} className="space-y-3">
            {/* Question */}
            <div
              className="p-4 bg-white rounded-md shadow-[0px_7px_26px_rgba(0,0,0,0.08)] border-l-4 border-blue-500"
              data-variable={questionKey}
              data-order-index={index}
            >
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xs font-medium text-blue-600 bg-blue-50 px-2 py-1 rounded">
                  Question #{index + 1}
                </span>
              </div>
              <p className="text-lg font-semibold leading-none">
                {faq.question}
              </p>
            </div>

            {/* Answer */}
            {faq.answer && (
              <div
                className="p-4 bg-gray-50 rounded-md shadow-[0px_7px_26px_rgba(0,0,0,0.08)] border-l-4 border-gray-400 ml-4"
                data-variable={answerKey}
                data-order-index={index}
              >
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xs font-medium text-gray-600 bg-gray-100 px-2 py-1 rounded">
                    Answer #{index + 1}
                  </span>
                </div>
                <p className="text-sm leading-6 text-gray-700">{faq.answer}</p>
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  return (
    <>
      {/* FAQPage structured data for SEO - enables rich FAQ snippets in search results */}
      <FAQSchema faqs={faqs} />
      <Container
        size="4"
        p="8"
        className={
          withBackground
            ? `relative min-h-[200px] overflow-hidden before:content-[''] before:absolute before:inset-0 before:bg-[url('/default/rental-steps-bg.svg')] before:bg-cover before:bg-center before:bg-no-repeat before:opacity-100 before:-z-10 after:content-[''] after:absolute after:inset-0 after:opacity-90 after:-z-10`
            : ''
        }
      >
        <Grid
        columns={{ initial: '1', xs: accordionOnly ? '1' : '2' }}
        gap="4"
        className="no-animate"
      >
        <Flex direction="column" gap="2" className="no-animate">
          <Text
            size="2"
            weight="bold"
            className="text-neutral-400"
            data-variable="FAQ_SUBTITLE"
          >
            {subtitle}
          </Text>
          <Heading
            as="h2"
            size="8"
            className="whitespace-normal"
            data-variable="FAQ_TITLE"
          >
            {title}
          </Heading>
          {customDescription && (
            <Text size="2" data-variable="FAQ_DESCRIPTION">
              {customDescription}
            </Text>
          )}
        </Flex>

        {!accordionOnly && (
          <Flex direction="column" gap="2">
            <Heading as="h3" size="6" data-variable="RENTAL_MEDIA_TITLE">
              {rentalVideoTitle}
            </Heading>
            <Flex direction="row" className="flex-nowrap">
              <Text
                size="3"
                className="leading-7"
                data-variable="RENTAL_MEDIA_SUBTITLE"
              >
                {rentalVideoSubtitle}
              </Text>
              <Box className="flex-shrink-0 self-end ml-2">
                <IconArrowDown />
              </Box>
            </Flex>
          </Flex>
        )}
      </Grid>

      <Grid
        columns={{ initial: '1', xs: accordionOnly ? '1' : '2' }}
        gap="4"
        className="mt-4 no-animate"
      >
        <Box className="relative isolate no-animate">
          {isEditMode ? (
            renderEditModeFAQs()
          ) : (
            <Accordion.Root type="single" collapsible className="space-y-4">
              {faqs.map((faq, index) => (
                <Accordion.Item
                  key={`${questionKey}-${index}`}
                  value={`${questionKey}-${index}`}
                  className="bg-white rounded-md"
                >
                  <Accordion.Trigger
                    className="text-left group flex w-full justify-between gap-5 px-6 py-6 
                  text-lg font-semibold leading-none rounded-md bg-white border-0
                  shadow-[0px_7px_26px_rgba(0,0,0,0.08)] 
                  hover:bg-[var(--background-color-light)] 
                  data-[state=open]:bg-[var(--background-color-light)]"
                    data-variable={questionKey}
                    data-order-index={index}
                  >
                    <span>{faq.question}</span>
                    <span className="shrink-0">
                      <PlusIcon className="w-6 h-6 transition-transform duration-200 group-data-[state=open]:hidden text-[var(--primary-color,#3463F8)] group-hover:text-[var(--primary-color,#3463F8)]" />
                      <MinusIcon className="w-6 h-6 transition-transform duration-200 hidden group-data-[state=open]:block text-[var(--primary-color,#3463F8)]" />
                    </span>
                  </Accordion.Trigger>
                  {faq.answer && (
                    <Accordion.Content
                      className={cn(
                        'overflow-hidden transition-all border-0',
                        'data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down',
                        'px-6 py-6 mt-0 bg-white rounded-md shadow-[0px_7px_26px_rgba(0,0,0,0.08)]',
                        'no-animate',
                      )}
                      data-variable={answerKey}
                      data-order-index={index}
                    >
                      <Text size="3" className="leading-7">
                        {faq.answer}
                      </Text>
                    </Accordion.Content>
                  )}
                </Accordion.Item>
              ))}
            </Accordion.Root>
          )}
        </Box>

        {!accordionOnly && (
          <Box className="relative isolate h-fit">
            {isImage && (
              <Image
                src={rentalVideoUrl}
                alt="Dumpster rental guide image"
                className="rounded-xl w-full shadow-[0px_7px_26px_rgba(0,0,0,0.08)]"
                data-variable="RENTAL_MEDIA_URL"
              />
            )}
            {isVideo && (
              <iframe
                height="300"
                src={embedUrl}
                title="Dumpster rental guide video"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                referrerPolicy="strict-origin-when-cross-origin"
                allowFullScreen
                className="rounded-xl w-full shadow-[0px_7px_26px_rgba(0,0,0,0.08)]"
                data-variable="RENTAL_MEDIA_URL"
              />
            )}
          </Box>
        )}
      </Grid>
      </Container>
    </>
  );
};

export default FAQ;
